import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, CodeIcon, ClockIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Navbar } from "../../components/common/Navbar";

export const UnderDevelopmentPage: React.FC = () => {
  const { featureName } = useParams<{ featureName: string }>();
  const navigate = useNavigate();

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  // Format feature name for display
  const formatFeatureName = (name: string | undefined) => {
    if (!name) return "Fitur";

    // Convert kebab-case or snake_case to Title Case
    return name
      .replace(/[-_]/g, " ")
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const displayFeatureName = formatFeatureName(featureName);

  return (
    <div className="bg-[#eeeff6] w-full min-h-screen flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Main Content with top padding to account for fixed navbar */}
      <main className="pt-[70px] px-4 sm:px-6 pb-6 flex-1">
        <div className="w-full max-w-[1392px] mx-auto mt-4 sm:mt-8 bg-white rounded-[21px] border-b border-[#f0eff0] relative p-4 sm:p-8 min-h-[calc(100vh-70px)] overflow-hidden flex items-center justify-center">
          {/* Background decorative elements - Hidden on mobile */}
          <div className="w-[328px] top-1 left-[-286px] bg-[#ffea9326] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0 hidden sm:block" />
          <div className="w-[328px] top-0 left-[380px] bg-[#f7f8ff] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0 hidden sm:block" />
          <div className="w-[328px] top-0 left-[790px] bg-[#f7f8ff] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0 hidden sm:block" />
          <div className="w-[329px] top-0 left-[62px] bg-[#eba0ff12] rounded-[164.67px/295.13px] absolute h-[590px] blur-[109.58px] z-0 hidden sm:block" />

          {/* Development Content */}
          <div className="text-center max-w-2xl relative z-10 w-full">
            {/* Back button */}
            <div className="flex justify-start mb-4 sm:mb-8">
              <Button
                onClick={handleBackToDashboard}
                variant="ghost"
                className="flex items-center gap-2 text-[#373e46] hover:bg-gray-100 p-2 rounded-lg w-auto"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </Button>
            </div>

            {/* Development Icon */}
            <div className="flex justify-center mb-4 sm:mb-8">
              <div className="relative">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-full flex items-center justify-center shadow-lg">
                  <CodeIcon className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
                </div>
                {/* Animated pulse ring */}
                <div className="absolute inset-0 w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-full animate-ping opacity-20"></div>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-2xl sm:text-4xl font-bold text-[#373e46] mb-2 sm:mb-4 leading-tight">
              {displayFeatureName} Sedang Dalam Pengembangan
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-xl text-[#373e46] mb-4 sm:mb-8 leading-relaxed px-2">
              Fitur ini sedang dalam tahap pengembangan dan akan segera
              tersedia. Tim kami bekerja keras untuk menghadirkan pengalaman
              terbaik untuk Anda.
            </p>

            {/* Status Card */}
            <div className="bg-gradient-to-r from-[#f8f9ff] to-[#f3f4ff] border border-[#e3e6eb] rounded-2xl p-4 sm:p-8 mb-4 sm:mb-8 shadow-sm">
              <div className="flex items-center justify-center gap-3 mb-4">
                <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#6b2cfe]" />
                <h3 className="text-base sm:text-lg font-semibold text-[#373e46]">
                  Status Pengembangan
                </h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#373e46] font-medium text-sm sm:text-base">
                    Fitur:
                  </span>
                  <span className="text-[#6b2cfe] font-semibold text-sm sm:text-base">
                    {displayFeatureName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#373e46] font-medium text-sm sm:text-base">
                    Status:
                  </span>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                    Dalam Pengembangan
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#373e46] font-medium text-sm sm:text-base">
                    Ketersediaan:
                  </span>
                  <span className="text-[#373e46] text-sm sm:text-base">
                    Segera Hadir
                  </span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-center">
              <Button
                onClick={handleBackToDashboard}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1 text-sm sm:text-base"
              >
                Kembali ke Dashboard
              </Button>
            </div>

            {/* Footer Note */}
            <p className="text-xs sm:text-sm text-[#97959e] mt-4 sm:mt-8 px-2">
              Terima kasih atas kesabaran Anda. Kami akan segera meluncurkan
              fitur ini untuk meningkatkan pengalaman Anda.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};
